<template>
  <div id="programs" class="page-container">
    <div class="page-content">

      <div class="flex-row-center mt-16">
        <img src="../assets/images/navbar/ic_programs_active.png" class="icon-pages" alt="" aria-hidden="true" />
        <span class="page-title">{{ $t("page.programs") }}</span>
        <span class="page-des page-des-line">{{ $t("page.programs_description") }}</span>
      </div>

      <el-tabs class="style-bar" v-model="activeName">

        <!-- TEMPLATES -->
        <el-tab-pane :name="anchor.templates">
          <span slot="label" class="title-bar">Templates</span>

          <div v-if="activeName === anchor.templates" class="programs-tab-body">

            <!-- Hero row: two equal starting points — "start fresh" on the
                 left, "use the featured template" on the right. The blank-
                 canvas option used to be a quiet link at the bottom of the
                 tab; it now reads as a peer choice to the templated path. -->
            <div class="programs-hero">
              <div
                class="programs-hero-card programs-hero-card--scratch"
                role="button"
                tabindex="0"
                @click="startFromScratch"
                @keydown.enter.prevent="startFromScratch"
                @keydown.space.prevent="startFromScratch"
              >
                <div class="programs-hero-card__eyebrow">Blank canvas</div>
                <h2 class="programs-hero-card__title">Start from scratch</h2>
                <p class="programs-hero-card__desc">
                  Build your own program. Add sessions, topics, polls, and
                  questions on any schedule that fits your team.
                </p>
                <div class="programs-hero-card__cta">
                  <span class="el-button btn-6C30CC" aria-hidden="true">
                    <i class="el-icon-plus"/> New program
                  </span>
                </div>
              </div>

              <div
                v-if="featuredTemplate"
                class="programs-hero-card programs-hero-card--featured"
                role="button"
                tabindex="0"
                @click="useTemplate(featuredTemplate)"
                @keydown.enter.prevent="useTemplate(featuredTemplate)"
                @keydown.space.prevent="useTemplate(featuredTemplate)"
              >
                <div class="programs-hero-card__eyebrow programs-hero-card__eyebrow--cat">
                  {{ categoryLabel(featuredTemplate.category) }}
                </div>
                <h2 class="programs-hero-card__title">{{ featuredTemplate.title }}</h2>
                <p class="programs-hero-card__desc">{{ featuredTemplate.description }}</p>
                <div class="programs-hero-card__foot">
                  <div class="programs-hero-card__meta">
                    <span><strong>{{ featuredTemplate.duration }}</strong></span>
                    <span class="dot"></span>
                    <span><strong>{{ featuredTemplate.items }}</strong> items</span>
                  </div>
                  <span class="el-button btn-6C30CC" aria-hidden="true">
                    Use this template
                  </span>
                </div>
              </div>
            </div>

            <div class="programs-section-head">
              <h3>Other templates</h3>
              <span class="helper">Clone any template into your own Programs — your edits don't affect the original.</span>
            </div>

            <div class="programs-grid">
              <div
                v-for="tpl in otherTemplates"
                :key="tpl.id"
                class="programs-tile"
                :class="['cat-' + tpl.category]"
                role="button"
                tabindex="0"
                @click="useTemplate(tpl)"
                @keydown.enter.prevent="useTemplate(tpl)"
                @keydown.space.prevent="useTemplate(tpl)"
              >
                <div class="programs-tile__cat">{{ categoryLabel(tpl.category) }}</div>
                <div class="programs-tile__title">{{ tpl.title }}</div>
                <div class="programs-tile__desc">{{ tpl.description }}</div>
                <div class="programs-tile__meta">
                  <span><strong>{{ tpl.duration }}</strong></span>
                  <span class="dot"></span>
                  <span><strong>{{ tpl.items }}</strong> items</span>
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- PROGRAMS — your authored definitions -->
        <el-tab-pane :name="anchor.programs">
          <span slot="label" class="title-bar">
            Programs
            <span v-if="visiblePrograms.length" class="programs-tab-count">{{ visiblePrograms.length }}</span>
          </span>
          <div v-if="activeName === anchor.programs" class="programs-tab-body">
            <div v-if="programsLoading && !programs.length" class="programs-loading-card" aria-live="polite">
              <i class="el-icon-loading"/>
              <div>
                <div class="programs-loading-title">Loading programs</div>
                <div class="programs-loading-sub">Bringing in your reusable plans and run history…</div>
              </div>
            </div>
            <div v-else-if="programsError && !displayedPrograms.length" class="programs-error-card" role="alert">
              <div class="programs-error-icon"><i class="el-icon-warning-outline"/></div>
              <div class="programs-error-copy">
                <div class="programs-error-title">Programs couldn't load</div>
                <div class="programs-error-sub">{{ programsError }}</div>
              </div>
              <el-button size="small" :loading="programsLoading" @click="fetchPrograms">Try again</el-button>
            </div>
            <template v-else-if="displayedPrograms.length || programQuery">
              <div v-if="programsError" class="programs-inline-warning" role="status">
                <i class="el-icon-warning-outline"/>
                <span>{{ programsError }} Showing the most recent programs already loaded.</span>
                <button type="button" @click="fetchPrograms">Retry</button>
              </div>
              <div class="programs-list-toolbar">
                <span class="programs-list-helper">
                  {{ displayedPrograms.length }} {{ showArchived ? 'archived ' : '' }}program{{ displayedPrograms.length === 1 ? '' : 's' }}
                </span>
                <div class="programs-toolbar-actions">
                  <el-input
                    v-model="programQuery"
                    size="small"
                    clearable
                    prefix-icon="el-icon-search"
                    placeholder="Search programs"
                    class="programs-search"
                    aria-label="Search programs"
                  />
                  <el-button
                    v-if="archivedPrograms.length || showArchived"
                    size="small"
                    @click="showArchived = !showArchived"
                  >
                    <i :class="showArchived ? 'el-icon-back' : 'el-icon-box'"/>
                    {{ showArchived ? 'Active programs' : `Archived (${archivedPrograms.length})` }}
                  </el-button>
                  <el-button v-if="!showArchived" class="btn-6C30CC" size="small" @click="startFromScratch">
                    <i class="el-icon-plus"/> New program
                  </el-button>
                </div>
              </div>
              <el-table v-if="filteredPrograms.length" :data="filteredPrograms" class="programs-table">
                <el-table-column label="Program" min-width="300">
                  <template slot-scope="scope">
                    <button
                      type="button"
                      class="programs-row-link"
                      :disabled="scope.row.status === 'archived'"
                      @click="editProgram(scope.row)"
                    >{{ scope.row.title || 'Untitled program' }}</button>
                    <div v-if="scope.row.description" class="programs-row-sub">{{ scope.row.description }}</div>
                  </template>
                </el-table-column>
                <el-table-column label="Items" width="80">
                  <template slot-scope="scope">
                    <span class="programs-shape">
                      <strong>{{ (scope.row.items || []).length }}</strong>
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="Runs" width="100">
                  <template slot-scope="scope">
                    <span class="programs-shape">
                      <strong>{{ batchCountForProgram(scope.row.id) }}</strong>
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="Status" width="110">
                  <template slot-scope="scope">
                    <span class="programs-status-pill" :class="programStatusTone(scope.row.status)">
                      {{ programStatusLabel(scope.row.status) }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="Updated" width="130">
                  <template slot-scope="scope">{{ formatDate(scope.row.updatedAt) }}</template>
                </el-table-column>
                <el-table-column width="220" align="right">
                  <template slot-scope="scope">
                    <div class="programs-row-actions">
                      <!-- Only show the run CTA on ready programs.
                           Showing it disabled on drafts read as noise on a
                           row that can't be acted on anyway. -->
                      <el-button
                        v-if="scope.row.status === 'ready'"
                        size="mini"
                        class="btn-6C30CC programs-row-cta"
                        @click.stop="openBatchCreate(scope.row)"
                      >
                        <i class="el-icon-video-play"/> Start a run
                      </el-button>
                      <el-button
                        v-else-if="scope.row.status !== 'archived'"
                        size="mini"
                        class="programs-row-continue"
                        @click.stop="editProgram(scope.row)"
                      >Continue setup</el-button>
                      <el-dropdown
                        trigger="click"
                        @command="(c) => onProgramAction(c, scope.row)"
                        @click.native.stop
                      >
                        <button type="button" class="programs-row-kebab" aria-label="Program actions">
                          <i class="el-icon-more"/>
                        </button>
                        <el-dropdown-menu slot="dropdown" class="happily-dropdown-popper">
                          <el-dropdown-item v-if="scope.row.status === 'archived'" command="restore">
                            <i class="el-icon-refresh-left"/> Restore
                          </el-dropdown-item>
                          <el-dropdown-item v-else command="archive">
                            <i class="el-icon-box"/> Archive
                          </el-dropdown-item>
                          <el-dropdown-item command="delete" class="programs-action-danger">
                            <i class="el-icon-delete"/> Delete
                          </el-dropdown-item>
                        </el-dropdown-menu>
                      </el-dropdown>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
              <div v-else class="programs-empty-card programs-empty-card--compact">
                <div class="programs-empty-bubble"><i class="el-icon-search"/></div>
                <div class="programs-empty-title">No matching programs</div>
                <div class="programs-empty-sub">Try a different name or clear the search.</div>
                <el-button size="small" @click="programQuery = ''">Clear search</el-button>
              </div>
            </template>
            <div v-else class="programs-empty-card">
              <div class="programs-empty-bubble">
                <i class="el-icon-edit-outline"/>
              </div>
              <div class="programs-empty-title">{{ showArchived ? 'No archived programs' : 'No programs yet' }}</div>
              <div class="programs-empty-sub">
                {{ showArchived
                  ? 'Programs you archive will appear here, ready to restore whenever you need them.'
                  : 'Programs are reusable plans — sessions, topics, polls and questions scheduled by relative day. Pick a template or start with a blank canvas.' }}
              </div>
              <div v-if="!showArchived" class="programs-empty-actions">
                <el-button class="btn-6C30CC programs-empty-cta" @click="startFromScratch">
                  <i class="el-icon-plus"/> New program
                </el-button>
                <el-button class="programs-empty-secondary" size="small" @click="goTab('templates')">
                  Browse templates
                </el-button>
              </div>
              <el-button v-else size="small" @click="showArchived = false">Back to active programs</el-button>
            </div>
          </div>
        </el-tab-pane>

        <!-- BATCHES — running and completed instances -->
        <el-tab-pane :name="anchor.batches">
          <span slot="label" class="title-bar">
            Runs
            <span v-if="batches.length" class="programs-tab-count">{{ batches.length }}</span>
          </span>
          <div v-if="activeName === anchor.batches" class="programs-tab-body">
            <div v-if="batchesLoading && !batches.length" class="programs-loading-card" aria-live="polite">
              <i class="el-icon-loading"/>
              <div>
                <div class="programs-loading-title">Loading runs</div>
                <div class="programs-loading-sub">Checking scheduled, active, and completed runs…</div>
              </div>
            </div>
            <div v-else-if="batchesError && !batches.length" class="programs-error-card" role="alert">
              <div class="programs-error-icon"><i class="el-icon-warning-outline"/></div>
              <div class="programs-error-copy">
                <div class="programs-error-title">Runs couldn't load</div>
                <div class="programs-error-sub">{{ batchesError }}</div>
              </div>
              <el-button size="small" :loading="batchesLoading" @click="fetchBatches">Try again</el-button>
            </div>
            <template v-else-if="batches.length">
              <div v-if="batchesError" class="programs-inline-warning" role="status">
                <i class="el-icon-warning-outline"/>
                <span>{{ batchesError }} Showing the most recent runs already loaded.</span>
                <button type="button" @click="fetchBatches">Retry</button>
              </div>
              <div class="programs-list-toolbar">
                <span class="programs-list-helper">
                  <span v-if="counts.activeBatches">
                    <strong>{{ counts.activeBatches }}</strong> active
                  </span>
                  <span v-if="counts.scheduledBatches" class="programs-list-helper-sep">
                    · <strong>{{ counts.scheduledBatches }}</strong> scheduled
                  </span>
                  <span v-if="counts.completedBatches" class="programs-list-helper-sep">
                    · <strong>{{ counts.completedBatches }}</strong> completed
                  </span>
                  <span v-if="counts.failedBatches" class="programs-list-helper-sep programs-list-helper-warn">
                    · <strong>{{ counts.failedBatches }}</strong> failed
                  </span>
                </span>
                <div class="programs-toolbar-actions">
                  <el-input
                    v-model="batchQuery"
                    size="small"
                    clearable
                    prefix-icon="el-icon-search"
                    placeholder="Search runs"
                    class="programs-search"
                    aria-label="Search runs"
                  />
                  <el-select v-model="batchStatusFilter" size="small" class="programs-status-filter" aria-label="Filter runs by status">
                    <el-option label="All statuses" value="all"/>
                    <el-option label="Active" value="active"/>
                    <el-option label="Scheduled" value="scheduled"/>
                    <el-option label="Completed" value="completed"/>
                    <el-option label="Failed" value="process_failed"/>
                  </el-select>
                </div>
              </div>
              <el-table v-if="filteredBatches.length" :data="filteredBatches" class="programs-table">
                <el-table-column label="Run" min-width="280">
                  <template slot-scope="scope">
                    <button type="button" class="programs-row-link" @click="openBatchProgress(scope.row)">
                      {{ scope.row.name }}
                    </button>
                    <div class="programs-row-sub">From <strong>{{ scope.row.programTitle }}</strong></div>
                  </template>
                </el-table-column>
                <el-table-column label="Audience" width="180">
                  <template slot-scope="scope">
                    <span class="programs-shape">{{ audienceSummary(scope.row) }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="Start date" width="140">
                  <template slot-scope="scope">{{ formatDate(scope.row.startDate) }}</template>
                </el-table-column>
                <el-table-column label="Status" width="120">
                  <template slot-scope="scope">
                    <el-tooltip
                      v-if="scope.row.status === 'process_failed'"
                      effect="dark"
                      placement="top"
                      :content="batchFailureReason(scope.row)"
                    >
                      <span class="programs-status-pill" :class="batchStatusTone(scope.row.status)">
                        {{ batchStatusLabel(scope.row.status) }}
                      </span>
                    </el-tooltip>
                    <span v-else class="programs-status-pill" :class="batchStatusTone(scope.row.status)">
                      {{ batchStatusLabel(scope.row.status) }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="Tracking" width="160">
                  <template slot-scope="scope">
                    <button type="button" class="programs-tracking-link" @click="openBatchProgress(scope.row)">
                      {{ batchTrackingLabel(scope.row) }} <i class="el-icon-arrow-right"/>
                    </button>
                  </template>
                </el-table-column>
                <el-table-column width="64" align="right">
                  <template slot-scope="scope">
                    <el-dropdown
                      trigger="click"
                      @command="(c) => onBatchAction(c, scope.row)"
                      @click.native.stop
                    >
                      <button type="button" class="programs-row-kebab" aria-label="Run actions">
                        <i class="el-icon-more"/>
                      </button>
                      <el-dropdown-menu slot="dropdown" class="happily-dropdown-popper">
                        <el-dropdown-item
                          v-if="scope.row.status === 'process_failed'"
                          command="retry"
                        >
                          <i class="el-icon-refresh"/> Retry run
                        </el-dropdown-item>
                        <el-dropdown-item command="delete" class="programs-action-danger">
                          <i class="el-icon-delete"/> Delete run
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </template>
                </el-table-column>
              </el-table>
              <div v-else class="programs-empty-card programs-empty-card--compact">
                <div class="programs-empty-bubble"><i class="el-icon-search"/></div>
                <div class="programs-empty-title">No matching runs</div>
                <div class="programs-empty-sub">Try a different name or status.</div>
                <el-button size="small" @click="clearBatchFilters">Clear filters</el-button>
              </div>
            </template>
            <div v-else class="programs-empty-card">
              <div class="programs-empty-bubble">
                <i class="el-icon-time"/>
              </div>
              <div class="programs-empty-title">No runs yet</div>
              <div class="programs-empty-sub">
                A run delivers one program to a specific group. {{ visiblePrograms.length ? 'Open a ready program and choose “Start a run”.' : 'Build a program first — pick a template or start from scratch.' }}
              </div>
              <el-button
                class="btn-6C30CC programs-empty-cta"
                @click="goTab(visiblePrograms.length ? 'programs' : 'templates')"
              >
                {{ visiblePrograms.length ? 'Go to programs' : 'Browse templates' }}
              </el-button>
            </div>
          </div>
        </el-tab-pane>

      </el-tabs>

    </div>

    <program-editor
      v-model="editorOpen"
      :initial-program="editorProgram"
      :template-install-busy="templateInstallBusy"
      :save-handler="handleSaveProgram"
      @install-template-content="handleInstallTemplateContent"
    />

    <template-install-modal
      v-model="templateInstallOpen"
      :template="templateInstallTemplate"
      :preview="templateInstallPreview"
      :loading="templateInstallPreviewLoading"
      :installing="templateInstallBusy"
      :preview-error="templateInstallPreviewError"
      @confirm="handleTemplateInstallConfirm"
    />

    <batch-create-modal
      v-model="batchCreateOpen"
      :program="batchCreateProgram"
      :create-handler="handleBatchCreate"
    />

    <batch-progress
      v-model="batchProgressOpen"
      :batch="batchProgressBatch"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ProgramEditor from './programs/program-editor.vue'
import BatchCreateModal from './programs/batch-create-modal.vue'
import BatchProgress from './programs/batch-progress.vue'
import TemplateInstallModal from './programs/template-install-modal.vue'
import { formatBatchFailure } from './programs/_program-labels'

const CATEGORY_LABELS = {
  onboarding: 'Onboarding',
  leadership: 'Leadership',
  compliance: 'Compliance',
  wellbeing: 'Well-being',
}

export default {
  name: 'programs',
  components: { ProgramEditor, BatchCreateModal, BatchProgress, TemplateInstallModal },
  computed: {
    ...mapGetters('programs', [
      'templates',
      'featuredTemplate',
      'programs',
      'batches',
      'counts',
      'batchCountForProgram',
      'loadingPrograms',
      'loadingBatches',
      'programsError',
      'batchesError',
    ]),
    programsLoading() {
      return this.loadingPrograms
    },
    batchesLoading() {
      return this.loadingBatches
    },
    user() {
      return this.$store.getters['authen/user']
    },
    otherTemplates() {
      return this.templates.filter((t) => !t.featured)
    },
    // Archived programs stay in Firestore but drop out of the list.
    visiblePrograms() {
      return this.programs.filter((p) => p.status !== 'archived')
    },
    archivedPrograms() {
      return this.programs.filter((p) => p.status === 'archived')
    },
    displayedPrograms() {
      return this.showArchived ? this.archivedPrograms : this.visiblePrograms
    },
    filteredPrograms() {
      const query = this.programQuery.trim().toLowerCase()
      if (!query) return this.displayedPrograms
      return this.displayedPrograms.filter((program) => (
        String(program.title || '').toLowerCase().includes(query) ||
        String(program.description || '').toLowerCase().includes(query)
      ))
    },
    filteredBatches() {
      const query = this.batchQuery.trim().toLowerCase()
      return this.batches.filter((batch) => {
        const matchesStatus = this.batchStatusFilter === 'all' || batch.status === this.batchStatusFilter
        const matchesQuery = !query ||
          String(batch.name || '').toLowerCase().includes(query) ||
          String(batch.programTitle || '').toLowerCase().includes(query)
        return matchesStatus && matchesQuery
      })
    },
  },
  watch: {
    activeName() {
      if (this.activeName !== '') {
        const tab = this.activeName || ''
        const paramsTab = this.$router.currentRoute.params.tab
        if (paramsTab !== tab) {
          this.$router.push({ name: 'programs', params: { tab } })
        }
      }
    },
  },
  data() {
    return {
      activeName: 'templates',
      editorOpen: false,
      editorProgram: null,
      batchCreateOpen: false,
      batchCreateProgram: null,
      batchProgressOpen: false,
      batchProgressBatch: null,
      templateInstallOpen: false,
      templateInstallTemplate: null,
      templateInstallPreview: null,
      templateInstallPreviewError: '',
      templateInstallPreviewLoading: false,
      templateInstallBusy: false,
      showArchived: false,
      programQuery: '',
      batchQuery: '',
      batchStatusFilter: 'all',
      anchor: {
        templates: 'templates',
        programs: 'programs',
        batches: 'batches',
      },
    }
  },
  mounted() {
    this.fetchTemplates().catch(() => {})
    this.fetchPrograms()
    this.fetchBatches()
    // Warm the library and audience getters so authors don't see empty
    // pickers when they open New program or Start a run before having
    // visited Town Hall / University / Questions / Members.
    this.$store.dispatch('happilyvillageUniversity/getUniversity').catch(() => {})
    this.$store.dispatch('happilyvillageTownhall/getTownhall').catch(() => {})
    this.$store.dispatch('question/getQuestions', {}).catch(() => {})
    this.$store.dispatch('custom/getQuestionsCustom', {}).catch(() => {})
    this.$store.dispatch('question/getUser', {}).catch(() => {})
    this.$store.dispatch('question/getChildren', {})
      .then(() => this.$store.dispatch('question/getTeamAndCohort', {}))
      .catch(() => {})
    const route = this.$router.currentRoute
    const params = route.params.tab
    if (params && this.anchor[params]) {
      this.activeName = params
    } else {
      this.$router.push({ name: 'programs', params: { tab: 'templates' } })
    }
  },
  methods: {
    ...mapActions('programs', [
      'fetchTemplates',
      'fetchPrograms',
      'fetchBatches',
      'cloneTemplate',
      'previewTemplateInstall',
      'installTemplate',
      'saveProgram',
      'createBatch',
      'archiveProgram',
      'restoreProgram',
      'deleteProgram',
      'deleteBatch',
      'retryBatch',
    ]),
    categoryLabel(key) {
      return CATEGORY_LABELS[key] || key
    },
    goTab(tab) {
      this.activeName = tab
    },
    async useTemplate(tpl) {
      if (!tpl) return this.startFromScratch()
      this.templateInstallTemplate = tpl
      this.templateInstallPreview = null
      this.templateInstallPreviewError = ''
      this.templateInstallOpen = true
      this.templateInstallPreviewLoading = true
      try {
        this.templateInstallPreview = await this.previewTemplateInstall(tpl.id)
      } catch (err) {
        // If preview cannot read the install map, still let the admin
        // install; the backend is the source of truth for reuse/create.
        this.templateInstallPreview = null
        this.templateInstallPreviewError = "We couldn't check which library items already exist. Install will still safely reuse matches and report the final result."
      } finally {
        this.templateInstallPreviewLoading = false
      }
    },
    async handleTemplateInstallConfirm(payload) {
      if (!payload || !payload.templateId) return
      this.templateInstallBusy = true
      try {
        const result = await this.installTemplate(payload)
        this.templateInstallOpen = false
        this.editorProgram = result.program
        this.editorOpen = true
        await this.refreshProgramLibraries()
        const created = (result.createdItems || []).length
        const reused = (result.reusedItems || []).length
        const suffix = created || reused
          ? ` Created ${created}, reused ${reused}.`
          : ''
        this.$message({
          message: `“${(result.program && result.program.title) || 'Program'}” installed.${suffix}`,
          type: result.errors && result.errors.length ? 'warning' : 'success',
        })
      } catch (err) {
        this.$message({
          message: `Template install failed: ${(err && err.message) || 'unknown error'}`,
          type: 'error',
        })
      } finally {
        this.templateInstallBusy = false
      }
    },
    async handleInstallTemplateContent(program) {
      if (!program || !program.templateRef) return
      this.templateInstallBusy = true
      try {
        const result = await this.installTemplate({
          templateId: program.templateRef,
          programId: program.id,
          title: program.title,
          description: program.description,
        })
        this.editorProgram = result.program
        await this.refreshProgramLibraries()
        this.$message({
          message: `Missing template items created for “${(result.program && result.program.title) || 'Program'}”.`,
          type: result.errors && result.errors.length ? 'warning' : 'success',
        })
      } catch (err) {
        this.$message({
          message: `Template content install failed: ${(err && err.message) || 'unknown error'}`,
          type: 'error',
        })
      } finally {
        this.templateInstallBusy = false
      }
    },
    startFromScratch() {
      this.editorProgram = null
      this.editorOpen = true
    },
    editProgram(program) {
      this.editorProgram = program
      this.editorOpen = true
    },
    openBatchCreate(program) {
      this.batchCreateProgram = program
      this.batchCreateOpen = true
    },
    openBatchProgress(batch) {
      this.batchProgressBatch = batch
      this.batchProgressOpen = true
    },
    async handleSaveProgram(program) {
      const saved = await this.saveProgram(program)
      this.$message({
        message: saved.status === 'ready'
          ? `“${saved.title || 'Untitled'}” saved — ready to run.`
          : `“${saved.title || 'Untitled'}” saved as draft.`,
        type: 'success',
      })
      return saved
    },
    refreshProgramLibraries() {
      return Promise.all([
        this.$store.dispatch('happilyvillageUniversity/getUniversity').catch(() => {}),
        this.$store.dispatch('happilyvillageTownhall/getTownhall').catch(() => {}),
        this.$store.dispatch('question/getQuestions', { forceRefresh: true }).catch(() => {}),
        this.$store.dispatch('custom/getQuestionsCustom', { forceRefresh: true }).catch(() => {}),
        this.$store.dispatch('sparks/getSparkCustom', this.user).catch(() => {}),
      ])
    },
    onProgramAction(command, program) {
      if (command === 'archive') this.confirmArchiveProgram(program)
      else if (command === 'restore') this.confirmRestoreProgram(program)
      else if (command === 'delete') this.confirmDeleteProgram(program)
    },
    async confirmRestoreProgram(program) {
      const name = program.title || 'Untitled program'
      try {
        await this.restoreProgram(program.id)
        this.$message({ message: `“${name}” restored.`, type: 'success' })
      } catch (err) {
        this.$message({ message: `Could not restore “${name}”. Try again.`, type: 'error' })
      }
    },
    confirmArchiveProgram(program) {
      const name = program.title || 'Untitled program'
      this.$confirm(
        `Archive “${name}”? It will be hidden from this list. Any existing runs will keep working.`,
        'Archive program',
        { confirmButtonText: 'Archive', cancelButtonText: 'Cancel', type: 'warning' },
      )
        .then(async () => {
          await this.archiveProgram(program.id)
          this.$message({ message: `“${name}” archived.`, type: 'success' })
        })
        .catch((action) => {
          if (action === 'cancel' || action === 'close') return
          this.$message({ message: `Could not archive “${name}”. Try again.`, type: 'error' })
        })
    },
    confirmDeleteProgram(program) {
      const name = program.title || 'Untitled program'
      const batches = this.batchCountForProgram(program.id)
      const warn = batches > 0
        ? ` Its ${batches} run${batches === 1 ? '' : 's'} will keep working — they aren't deleted.`
        : ''
      this.$confirm(
        `Delete “${name}”? This can't be undone.${warn}`,
        'Delete program',
        { confirmButtonText: 'Delete', cancelButtonText: 'Cancel', type: 'warning' },
      )
        .then(async () => {
          await this.deleteProgram(program.id)
          this.$message({ message: `“${name}” deleted.`, type: 'success' })
        })
        .catch((action) => {
          if (action === 'cancel' || action === 'close') return
          this.$message({ message: `Could not delete “${name}”. Try again.`, type: 'error' })
        })
    },
    onBatchAction(command, batch) {
      if (command === 'delete') this.confirmDeleteBatch(batch)
      else if (command === 'retry') this.confirmRetryBatch(batch)
    },
    confirmRetryBatch(batch) {
      const name = batch.name || 'this run'
      const { friendly, technical } = this.batchFailureDetail(batch)
      const technicalBlock = technical
        ? `<div class="programs-retry-tech">
             <div class="programs-retry-tech-label">Technical details (share with support if it keeps failing)</div>
             <code class="programs-retry-tech-code">${this.escapeHtml(technical)}</code>
           </div>`
        : ''
      const message = `
        <div class="programs-retry-msg">
          <p class="programs-retry-lead">What happened</p>
          <p class="programs-retry-body">${this.escapeHtml(friendly)}</p>
          <p class="programs-retry-lead">What "Retry" does</p>
          <p class="programs-retry-body">We'll clear the failed attempt and start <strong>${this.escapeHtml(name)}</strong> fresh with the same program, start date, and audience.</p>
          ${technicalBlock}
        </div>
      `
      this.$confirm(message, 'Retry run', {
        confirmButtonText: 'Retry',
        cancelButtonText: 'Cancel',
        type: 'info',
        dangerouslyUseHTMLString: true,
        customClass: 'programs-retry-confirm',
      })
        .then(async () => {
          try {
            await this.retryBatch(batch.id)
            this.$message({ message: `“${name}” restarted.`, type: 'success' })
          } catch (err) {
            this.$message({ message: `Retry failed: ${(err && err.message) || 'unknown error'}`, type: 'error' })
          }
        })
        .catch(() => {})
    },
    confirmDeleteBatch(batch) {
      const name = batch.name || 'this run'
      const count = (batch.enrolees || []).length
      const who = count
        ? ` Everything it delivered to ${count} ${count === 1 ? 'person' : 'people'} will be removed from their app.`
        : ''
      this.$confirm(
        `Delete “${name}”? This can't be undone.${who}`,
        'Delete run',
        { confirmButtonText: 'Delete', cancelButtonText: 'Cancel', type: 'warning' },
      )
        .then(async () => {
          await this.deleteBatch(batch.id)
          this.$message({ message: `“${name}” deleted.`, type: 'success' })
        })
        .catch((action) => {
          if (action === 'cancel' || action === 'close') return
          this.$message({ message: `Could not delete “${name}”. Try again.`, type: 'error' })
        })
    },
    async handleBatchCreate(payload) {
      try {
        const batch = await this.createBatch(payload)
        this.$message({
          message: batch.status === 'scheduled'
            ? `“${batch.name}” scheduled.`
            : `“${batch.name}” started.`,
          type: 'success',
        })
        this.activeName = 'batches'
        return batch
      } catch (err) {
        this.$message({ message: 'This run could not be created. Your details are still here.', type: 'error' })
        throw err
      }
    },
    programStatusTone(status) {
      switch (status) {
        case 'ready':    return 'is-ready'
        case 'archived': return 'is-archived'
        default:         return 'is-draft'
      }
    },
    programStatusLabel(status) {
      switch (status) {
        case 'ready':    return 'Ready'
        case 'archived': return 'Archived'
        default:         return 'Draft'
      }
    },
    batchStatusTone(status) {
      switch (status) {
        case 'active':         return 'is-active'
        case 'scheduled':      return 'is-scheduled'
        case 'completed':      return 'is-completed'
        case 'process_failed': return 'is-failed'
        case 'archived':       return 'is-draft'
        default:               return 'is-draft'
      }
    },
    batchStatusLabel(status) {
      if (!status) return 'Draft'
      if (status === 'process_failed') return 'Failed'
      return status.charAt(0).toUpperCase() + status.slice(1)
    },
    batchFailureReason(batch) {
      // Tooltip on the status pill. Keep it short — the long technical
      // detail is reserved for the retry confirm dialog.
      const { friendly } = formatBatchFailure(batch && batch.processFailedReason)
      return friendly
    },
    batchFailureDetail(batch) {
      // Full {friendly, technical} pair for the retry confirm dialog.
      return formatBatchFailure(batch && batch.processFailedReason)
    },
    escapeHtml(s) {
      // The retry confirm dialog uses HTML to lay out friendly text +
      // technical detail block. processFailedReason can be raw user input
      // (a library doc title in some error paths) — escape before injection.
      return String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    audienceSummary(batch) {
      const sels = (batch.audienceSnapshot && batch.audienceSnapshot.selections) || []
      if (!sels.length) return '—'
      const counts = sels.reduce((acc, s) => {
        acc[s.kind] = (acc[s.kind] || 0) + 1
        return acc
      }, {})
      const parts = []
      if (counts.cohort)  parts.push(`${counts.cohort} cohort${counts.cohort === 1 ? '' : 's'}`)
      if (counts.team)    parts.push(`${counts.team} team${counts.team === 1 ? '' : 's'}`)
      if (counts.person)  parts.push(`${counts.person} ${counts.person === 1 ? 'person' : 'people'}`)
      return parts.join(' · ') || '—'
    },
    batchTrackingLabel(batch) {
      switch ((batch && batch.status) || '') {
        case 'completed':      return 'View results'
        case 'scheduled':      return 'View schedule'
        case 'process_failed': return 'Review issue'
        default:               return 'View live progress'
      }
    },
    clearBatchFilters() {
      this.batchQuery = ''
      this.batchStatusFilter = 'all'
    },
    formatDate(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      } catch (e) {
        return '—'
      }
    },
  },
}
</script>

<style>
/* Match the canonical .style-bar tab treatment used by insights, performance,
 * etc. — taller items with a fixed min-width so the bar doesn't read as a
 * cramped strip of three short links. */
#programs .el-tabs__header {
  margin: 0 0 8px;
}
#programs .el-tabs__item {
  height: 52px;
  line-height: 52px;
  min-width: 132px;
  padding: 0 20px;
  text-align: center;
  font-family: 'Lato', 'Kanit', Arial, sans-serif;
}

/* The shared .page-content card stretches to the full .page-container
 * height by default (flex align-items: stretch), leaving a large empty
 * white expanse on a sparse page. Let the card hug its content instead
 * — the gray page background fills the rest, so the card reads as a
 * deliberate surface rather than a half-filled panel. */
#programs.page-container {
  align-items: flex-start;
}

/* Row-actions menu is teleported out of scoped range by Element UI,
 * so the danger styling lives in this unscoped block. Selectors include
 * .el-dropdown-menu__item + :not(.is-disabled) so they out-specify
 * Element UI's own item:hover rule (which would otherwise paint a
 * destructive action in the default blue). */
.happily-dropdown-popper .el-dropdown-menu__item.programs-action-danger {
  color: #cc3030;
}
.happily-dropdown-popper .el-dropdown-menu__item.programs-action-danger i {
  color: inherit;
}
.happily-dropdown-popper .el-dropdown-menu__item.programs-action-danger:not(.is-disabled):hover,
.happily-dropdown-popper .el-dropdown-menu__item.programs-action-danger:not(.is-disabled):focus {
  background: #fdecec;
  color: #cc3030;
}

/* Retry batch confirm dialog. el-message-box renders outside this
 * component, so styles live in the unscoped block. Layout: a stack of
 * short headed paragraphs, with the technical detail set off in a
 * muted code block so it reads as "copy me to support" rather than
 * "this is the error you need to read." */
.programs-retry-confirm {
  width: 480px;
  max-width: calc(100vw - 32px);
}
.programs-retry-confirm .el-message-box__content {
  padding: 12px 16px 16px;
}
.programs-retry-msg .programs-retry-lead {
  font-family: 'Lato', 'Kanit', Arial, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a8a;
  margin: 12px 0 4px;
}
.programs-retry-msg .programs-retry-lead:first-child {
  margin-top: 0;
}
.programs-retry-msg .programs-retry-body {
  font-size: 13px;
  line-height: 1.5;
  color: #1a1a1a;
  margin: 0;
}
.programs-retry-tech {
  margin-top: 16px;
  padding: 10px 12px;
  border: 1px solid #eef0f5;
  border-radius: 8px;
  background: #fafbfd;
}
.programs-retry-tech-label {
  font-family: 'Lato', 'Kanit', Arial, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #8a8a8a;
  margin-bottom: 6px;
}
.programs-retry-tech-code {
  display: block;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12px;
  color: #1a1a1a;
  word-break: break-all;
  white-space: pre-wrap;
  user-select: all;
  -webkit-user-select: all;
}
</style>

<style scoped>
.programs-tab-body {
  padding: 8px 0 32px;
}

/* Tab pill counts */
.programs-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  margin-left: 6px;
  border-radius: 999px;
  background: #f2ecfb;
  color: #6c30cc;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  vertical-align: middle;
}

/* Hero row — two equal starting points */
.programs-hero {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 16px;
  margin-bottom: 32px;
}
.programs-hero-card {
  position: relative;
  border-radius: 16px;
  padding: 28px 28px 26px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
}
.programs-hero-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 1px 2px rgba(15,16,32,.04), 0 10px 28px rgba(15,16,32,.06);
}
.programs-hero-card:focus-visible,
.programs-tile:focus-visible {
  outline: 3px solid rgba(108, 48, 204, 0.28);
  outline-offset: 3px;
}
.programs-hero-card--scratch {
  background: #ffffff;
  border: 1.5px dashed rgba(108, 48, 204, 0.30);
}
.programs-hero-card--scratch:hover {
  border-color: rgba(108, 48, 204, 0.55);
  background: linear-gradient(135deg, rgba(108, 48, 204, 0.03) 0%, #ffffff 100%);
}
.programs-hero-card--featured {
  background:
    linear-gradient(135deg, rgba(108, 48, 204, 0.06) 0%, rgba(108, 48, 204, 0.015) 100%),
    #ffffff;
  border: 1px solid rgba(108, 48, 204, 0.10);
}
.programs-hero-card--featured:hover {
  border-color: rgba(108, 48, 204, 0.22);
}
.programs-hero-card__eyebrow {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #6c30cc;
  background: #f2ecfb;
  padding: 4px 10px;
  border-radius: 999px;
  align-self: flex-start;
}
.programs-hero-card__eyebrow--cat {
  color: #6c30cc;
}
.programs-hero-card__title {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.018em;
  color: #1a1a1a;
  line-height: 1.18;
  margin: 0;
}
.programs-hero-card__desc {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.55;
  margin: 0;
  flex: 1;
}
.programs-hero-card__cta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
}
.programs-hero-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 6px;
}
.programs-hero-card__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #6b7280;
  flex-wrap: wrap;
}
.programs-hero-card__meta strong { color: #1a1a1a; font-weight: 700; }
.programs-hero-card__meta .dot,
.programs-tile__meta .dot {
  width: 3px;
  height: 3px;
  border-radius: 999px;
  background: #c0c4cc;
}
@media (max-width: 960px) {
  .programs-hero { grid-template-columns: 1fr; }
  .programs-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .programs-grid { grid-template-columns: 1fr; }
  .programs-section-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }
}

/* Section heading */
.programs-section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 16px;
}
.programs-section-head h3 {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a8a;
  margin: 0;
}
.programs-section-head .helper {
  font-size: 13px;
  color: #6b7280;
}

/* Tile grid */
.programs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.programs-tile {
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 12px;
  padding: 22px 22px 20px;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
  display: flex;
  flex-direction: column;
  min-height: 248px;
}
.programs-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 1px 2px rgba(15,16,32,.04), 0 8px 24px rgba(15,16,32,.05);
  border-color: rgba(108,48,204,.20);
}
.programs-tile__cat {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #8a8a8a;
  margin-bottom: 6px;
}
.programs-tile__title {
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.005em;
  color: #1a1a1a;
  line-height: 1.3;
  margin-bottom: 6px;
}
.programs-tile__desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  flex: 1;
  margin-bottom: 16px;
  /* Cap at 3 lines so cards stay rhythmically aligned regardless of
   * description length — matches the line-clamp pattern used in
   * library-picker. */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.programs-tile__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  color: #8a8a8a;
  font-weight: 600;
  padding-top: 14px;
  border-top: 1px solid #eef0f5;
  margin-top: auto;
}
.programs-tile__meta strong { color: #6b7280; font-weight: 700; }

/* List view */
.programs-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  gap: 14px;
  flex-wrap: wrap;
}
.programs-toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}
.programs-search { width: 220px; }
.programs-status-filter { width: 150px; }
.programs-list-helper {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a8a;
}
.programs-list-helper strong { color: #1a1a1a; font-weight: 800; }
.programs-list-helper-sep { margin-left: 4px; }
.programs-list-helper-warn { color: #cc3030; }
.programs-list-helper-warn strong { color: #cc3030; }
.programs-table {
  width: 100%;
  border: 1px solid #e2e4ea;
  border-radius: 12px;
  overflow: hidden;
}
.programs-table >>> tbody tr { cursor: default; }
/* Element UI's default 12px cell padding reads cramped for two-line
 * title+subtitle rows on a spacious page — give the list comfortable
 * vertical rhythm. */
.programs-table >>> tbody td {
  padding-top: 16px;
  padding-bottom: 16px;
}
/* Keep the first/last columns off the card border — Element's 10px
 * default .cell padding lets the title and action buttons hug the
 * edge. Inner column gaps stay at the default; header cells get the
 * same inset so column labels stay aligned with the rows below. */
.programs-table >>> tr > :first-child .cell {
  padding-left: 20px;
}
.programs-table >>> tr > :last-child .cell {
  padding-right: 20px;
}
.programs-row-link,
.programs-tracking-link {
  appearance: none;
  padding: 0;
  border: 0;
  background: transparent;
  color: #1a1a1a;
  font: inherit;
  cursor: pointer;
  text-align: left;
}
.programs-row-link {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
}
.programs-row-link:hover,
.programs-row-link:focus-visible,
.programs-tracking-link:hover,
.programs-tracking-link:focus-visible {
  color: #6c30cc;
  text-decoration: underline;
  outline: none;
}
.programs-row-link:disabled {
  color: #6b7280;
  cursor: default;
  text-decoration: none;
}
.programs-tracking-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #6c30cc;
  font-size: 12px;
  font-weight: 700;
}
.programs-row-sub {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.45;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.programs-row-sub strong { color: #1a1a1a; font-weight: 700; }
.programs-shape {
  font-size: 12px;
  color: #6b7280;
}
.programs-shape strong { color: #1a1a1a; font-weight: 700; }
.programs-row-cta {
  white-space: nowrap;
}
.programs-row-continue {
  color: #6c30cc;
  border-color: rgba(108, 48, 204, 0.24);
  background: #fff;
}
.programs-row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.programs-row-kebab {
  appearance: none;
  background: transparent;
  border: 1px solid transparent;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  color: #8a8a8a;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background .15s ease, color .15s ease;
}
.programs-row-kebab:hover,
.programs-row-kebab:focus {
  background: #f2ecfb;
  color: #6c30cc;
  outline: none;
}

.programs-status-pill {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
}
.programs-status-pill.is-draft {
  color: #8a8a8a;
  background: #f6f7f9;
}
.programs-status-pill.is-ready {
  color: #6c30cc;
  background: #f2ecfb;
}
.programs-status-pill.is-scheduled {
  color: #c89500;
  background: rgba(247, 185, 36, 0.12);
}
.programs-status-pill.is-active {
  color: #00b383;
  background: rgba(0, 179, 131, 0.10);
}
.programs-status-pill.is-completed {
  color: #6c30cc;
  background: #f2ecfb;
}
.programs-status-pill.is-failed {
  color: #cc3030;
  background: rgba(204, 48, 48, 0.10);
}
.programs-status-pill.is-archived {
  color: #5f6673;
  background: #eef0f5;
}

.programs-loading-card,
.programs-error-card {
  min-height: 180px;
  padding: 30px;
  border: 1px solid #e2e4ea;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 16px;
}
.programs-loading-card > i {
  color: #6c30cc;
  font-size: 24px;
}
.programs-loading-title,
.programs-error-title {
  color: #1a1a1a;
  font-size: 15px;
  font-weight: 800;
}
.programs-loading-sub,
.programs-error-sub {
  margin-top: 4px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.45;
}
.programs-error-copy { flex: 1; }
.programs-error-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #fff1f1;
  color: #b42323;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.programs-inline-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 9px 12px;
  border: 1px solid #f1d48a;
  border-radius: 8px;
  background: #fffaf0;
  color: #715500;
  font-size: 12px;
}
.programs-inline-warning span { flex: 1; }
.programs-inline-warning button {
  appearance: none;
  border: 0;
  background: transparent;
  color: #6c30cc;
  font-weight: 800;
  cursor: pointer;
}

/* Empty state */
.programs-empty-card {
  text-align: center;
  padding: 56px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.programs-empty-card--compact { padding: 38px 24px; }
.programs-empty-bubble {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: #f6f7f9;
  border: 1px solid #eef0f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #8a8a8a;
  margin-bottom: 16px;
}
.programs-empty-title {
  font-size: 16px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 6px;
}
.programs-empty-sub {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.55;
  max-width: 480px;
  margin-bottom: 20px;
}
.programs-empty-cta {
  margin-top: 4px;
}
.programs-empty-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}
.programs-empty-secondary {
  margin-top: 4px;
}
@media (max-width: 720px) {
  #programs .el-tabs__item {
    min-width: 0;
    padding: 0 12px;
  }
  .programs-toolbar-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .programs-search,
  .programs-status-filter {
    width: 100%;
  }
  .programs-error-card {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .programs-section-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }
}
</style>
